import React, { useState } from 'react';
import { ApiRequestBuilderV2 } from '../../GlobalComponents/Builders';
import { Container, Card, CardBody, Badge } from 'reactstrap';
import AssociationMappingSection from '../Tools/Tool/Jobs/Job/DMT/components/sections/association/AssociationMappingSection';

/**
 * API Test Page — localhost only, admin access.
 *
 * Uses ApiRequestBuilderV2 which always returns:
 *   { success, data, status, error, method }
 *
 * Usage:
 *   test(name, method, endpoint, () => ApiRequestBuilderV2(...))
 *   Note: each test name must be unique — duplicate names will overwrite results.
 *
 * Examples:
 *
 *   test('List Services', 'GET', '/services',
 *       () => ApiRequestBuilderV2('servicesGet')
 *   );
 *
 *   test('Get Job', 'GET', '/dmt/jobs/123',
 *       () => ApiRequestBuilderV2('dmtJobsJobIdGet', { jobId: '123' })
 *   );
 *
 *   test('Record Count', 'POST', '/dmt/assessments/1/recordCount',
 *       () => ApiRequestBuilderV2('dmtAssessmentsAssessmentIdRecordCountPost', { assessmentId: '1' }, {
 *           connectionId: 202, resourceUri: 'company/companies',
 *           filters: [{ field: 'status/name', op: '=', value: 'Active' }]
 *       })
 *   );
 *
 * SDK naming: path + verb → camelCase
 *   /dmt/jobs/{jobId} + GET → dmtJobsJobIdGet
 */

const ApiTest = () => {
    const [results, setResults] = useState({});

    const test = (name, method, endpoint, fn) => {
        setResults(prev => ({ ...prev, [name]: { status: 'loading', method, endpoint } }));
        fn().then(res => {
            setResults(prev => ({ ...prev, [name]: { data: res, status: 'done', method, endpoint } }));
        });
    };

    const runTests = () => {
        setResults({});

        // ── Define your tests here ──

        test('Association Mappings - List', 'GET', '/dmt/jobs/11/associationMappings?sourceResourceId=69',
            () => ApiRequestBuilderV2('dmtJobsJobIdCompositeMappingsGet', {
                pathParams: { jobId: '11' },
                queryParams: { sourceResourceId: '69' },
            })
        );

        test('Association Mappings - Generate Templates', 'POST', '/dmt/jobs/11/associationMappings',
            () => ApiRequestBuilderV2('dmtJobsJobIdCompositeMappingsPost', {
                pathParams: { jobId: '11' },
                body: { action: 'generate' },
            })
        );

    };

    if (!window.location.hostname.includes('localhost')) {
        window.location.href = process.env.PUBLIC_URL + '/dashboard';
        return null;
    }

    const methodColors = { GET: 'info', POST: 'warning', PATCH: 'secondary', DELETE: 'danger' };
    const preStyle = { fontSize: 12, padding: 10, borderRadius: 4, border: '1px solid rgba(128,128,128,0.2)', margin: 0, background: 'var(--card-body-bg, #f8f9fa)', color: 'var(--body-font-color, #333)' };
    const hasTests = Object.keys(results).length > 0;

    return (
        <Container fluid>
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h4 className='mb-0'>APIv2 Test</h4>
                <button className='btn btn-sm btn-primary' onClick={runTests}>Run Tests</button>
            </div>

            {!hasTests && (
                <Card>
                    <CardBody>
                        <p className='text-muted mb-2'>No tests configured. Add calls inside <code>runTests()</code> in <code>src/Components/Dev/ApiTest.jsx</code></p>
                        <p className='text-muted mb-3' style={{ fontSize: 12 }}>Each test name must be unique — duplicate names overwrite results.</p>

                        <h6 className='mb-2'>Simple GET</h6>
                        <pre style={preStyle}>
{`test('List Services', 'GET', '/services',
    () => ApiRequestBuilderV2('servicesGet')
);`}
                        </pre>

                        <h6 className='mt-3 mb-2'>GET with path params</h6>
                        <pre style={preStyle}>
{`test('Get Job', 'GET', '/dmt/jobs/123',
    () => ApiRequestBuilderV2('dmtJobsJobIdGet', { pathParams: { jobId: '123' } })
);`}
                        </pre>

                        <h6 className='mt-3 mb-2'>GET with query params</h6>
                        <pre style={preStyle}>
{`test('Filter Keys', 'GET', '/dmt/assessments/1/filterableKeys?resourceId=97',
    () => ApiRequestBuilderV2('dmtAssessmentsAssessmentIdFilterableKeysGet', {
        pathParams: { assessmentId: '1' },
        queryParams: { resourceId: '97' },
    })
);`}
                        </pre>

                        <h6 className='mt-3 mb-2'>POST with body</h6>
                        <pre style={preStyle}>
{`test('Record Count', 'POST', '/dmt/assessments/1/recordCount',
    () => ApiRequestBuilderV2('dmtAssessmentsAssessmentIdRecordCountPost', {
        pathParams: { assessmentId: '1' },
        body: { connectionId: 202, resourceUri: 'company/companies',
                filters: [{ field: 'status/name', op: '=', value: 'Active' }] },
    })
);`}
                        </pre>

                        <h6 className='mt-3 mb-2'>SDK method naming</h6>
                        <p className='text-muted' style={{ fontSize: 12, marginBottom: 0 }}>
                            Path + HTTP verb → camelCase. <code>/dmt/jobs/&#123;jobId&#125;</code> + GET → <code>dmtJobsJobIdGet</code>
                        </p>
                    </CardBody>
                </Card>
            )}

            {Object.entries(results).map(([name, { data, status, method, endpoint }]) => {
                const passed = status === 'done' && data?.success;
                const failed = status === 'done' && !data?.success;
                return (
                    <Card key={name} className='mb-2' style={{ borderLeft: `4px solid ${passed ? '#28a745' : failed ? '#dc3545' : '#6c757d'}` }}>
                        <CardBody>
                            <div className='d-flex align-items-center gap-2 mb-2'>
                                {status === 'loading'
                                    ? <Badge color='secondary'>RUNNING</Badge>
                                    : <Badge color={passed ? 'success' : 'danger'}>{passed ? 'PASS' : 'FAIL'}</Badge>
                                }
                                <Badge color={methodColors[method] || 'secondary'}>{method}</Badge>
                                <strong>{name}</strong>
                                <code className='text-muted' style={{ fontSize: 11 }}>{endpoint}</code>
                                {data?.status ? <span className='text-muted' style={{ fontSize: 11 }}>{data.status}</span> : null}
                            </div>
                            {data && (
                                <pre style={{ fontSize: 12, maxHeight: 200, overflow: 'auto', background: 'var(--card-body-bg, #f8f9fa)', color: 'var(--body-font-color, #333)', padding: 10, borderRadius: 4, margin: 0, border: '1px solid var(--border-color, #dee2e6)' }}>
                                    {JSON.stringify(data.success ? data.data : data, null, 2)}
                                </pre>
                            )}
                        </CardBody>
                    </Card>
                );
            })}

            {/* Association Mapping Component */}
            <hr className='my-4' />
            <h5 className='mb-3'>Association Mapping Component (Job 11)</h5>
            <AssociationMappingSection jobData={{ id: 11 }} sourceResourceId={69} />
        </Container>
    );
};

export default ApiTest;
